import { Modal, Table, Button, TableColumnProps } from 'antd';
import classNames from 'classnames';
import { get } from 'lodash-es';
import ResizeObserver from 'rc-resize-observer';
import React, { FC, Fragment, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableSizeGrid as Grid } from 'react-window';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import { getProductId } from '@/infrastructure/product-info';
import { formatByte2Mb } from '@/infrastructure/utils/digit';
import { decimal2Percentile } from '@/infrastructure/utils/to-unit';
import { hintChildren } from '@/presentation/components/hint-text/hintText';
import { batchFetchMarketingNames } from '@/presentation/components/model-display/batch-marketing-name';
import { getTopData } from '@/presentation/components/trend/api';
import { SUB_METRIC_OPTIONS } from '@/presentation/pages/traffic/analytics/config';
import { IGetTopDataReq, MetricSubType, MetricType } from '@/proto/metric';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import styles from './index.module.scss';
export interface DetailViewProps {
  metricName: string;
  dimensionName: string;
  token: string;
  metricSubType: MetricSubType;
  selectMetricType: MetricSubType;
  timeWindow: number;
}

interface IData {
  total: number;
  list: IList[];
}
interface IList {
  title: string;
  rawDimensionValue?: string;
  count: number;
  countRatio: number;
  valueRatio: number;
  trafficAvg: number;
  trafficP25: number;
  trafficP50: number;
  trafficP75: number;
  trafficP90: number;
  trafficP99: number;
}

const sorterFunc = (
  a: IList,
  b: IList,
  path: string,
) => parseFloat(get(a, path))
  - parseFloat(get(b, path));

export const DetailView: FC<DetailViewProps> = (props) => {
  const { t } = useTranslation();
  const { dimensionName, token, metricSubType, selectMetricType, timeWindow } = props;
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<IData>();
  const [tableWidth, setTableWidth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marketingNameMap, setMarketingNameMap] = useState<Record<string, string>>({});
  const scroll = {
    y: 680,
    x: 1122,
  };
  useEffect(() => {
    if (visible) {
      load();
    } else {
      setData(undefined);
    }
  }, [visible]);


  const load = async () => {
    setLoading(true);
    const params: IGetTopDataReq = {
      productId: getProductId(),
      token,
      timeWindow,
      type: MetricType.TYPE_TRAFFIC_RESOURCE,
      subTypes: [metricSubType],
      dimensionNames: [dimensionName],
      extendOption: { sortField: selectMetricType, sortType: 'DESC' },
      limit: 3000,
    };

    const res = await getTopData(params);
    // 过滤field为空的数据
    const filterDistributions = res.records
      .filter((item, index) => !!item.dimensionValue && index < 3000);

    const distributions: any = filterDistributions.map?.((item: any) => {
      const record: Record<string, number> = {};
      const metricNames = res?.metricNames || [];
      const metricValues = item.metricValues || [];
      const total = res.total || 0;

      metricNames.forEach((name: string, index: number) => {
        record[toHumpString(name)] = metricValues[index];
      });

      return {
        title: item.dimensionValue || '',
        rawDimensionValue: item.dimensionValue || '',
        valueRatio: record.count ? record.count / total : 0,
        ...record,
      };
    });
    setData({ list: distributions, total: res.total || 0 });
    setLoading(false);
    // 机型维度：批量拉取营销名称
    if (dimensionName === 'model' && distributions.length) {
      const models = distributions.map((d: IList) => d.rawDimensionValue).filter(Boolean) as string[];
      batchFetchMarketingNames(models).then(setMarketingNameMap);
    } else {
      setMarketingNameMap({});
    }
  };
  const countName = SUB_METRIC_OPTIONS.find(item => item.value === selectMetricType)?.label;

  const columns: TableColumnProps<IList>[] = [
    {
      dataIndex: 'metricName',
      title: props.metricName,
      width: 280,
      key: 'metricName',
      render: (_: unknown, item) => {
        const marketingName = marketingNameMap[item.rawDimensionValue || ''];
        const displayText = marketingName ? `${item.title} (${marketingName})` : item.title;
        return hintChildren(<div className={'truncate'}>{displayText}</div>, displayText);
      },
    },
    {
      dataIndex: 'count',
      key: 'count',
      title: countName,
      width: 130,
      render: (_: unknown, item) => item.count,
      sorter: (a, b) => sorterFunc(a, b, 'count'),
    },
    {
      dataIndex: 'countRatio',
      key: 'countRatio',
      title: t('异常率'),
      width: 150,
      render: (_: unknown, item) => decimal2Percentile(item.countRatio, 2),
      sorter: (a, b) => sorterFunc(a, b, 'count'),
    },
    {
      dataIndex: 'valueRatio',
      key: 'valueRatio',
      title: t('占比'),
      width: 150,
      render: (_: unknown, item) => decimal2Percentile(item.valueRatio, 2),
      sorter: (a, b) => sorterFunc(a, b, 'count'),
    },
    {
      dataIndex: 'trafficAvg',
      key: 'trafficAvg',
      title: t('平均流量'),
      width: 130,
      render: (_: unknown, item) => formatByte2Mb(item.trafficAvg ?? 0),
      sorter: (a, b) => sorterFunc(a, b, 'trafficAvg'),
    },
    {
      dataIndex: 'trafficP25',
      key: 'trafficP25',
      title: t('流量P25'),
      width: 150,
      render: (_: unknown, item) => formatByte2Mb(item.trafficP25 ?? 0),
      sorter: (a, b) => sorterFunc(a, b, 'trafficP25'),
    },
    {
      dataIndex: 'trafficP50',
      title: t('流量P50'),
      key: 'trafficP50',
      width: 150,
      render: (_: unknown, item) => formatByte2Mb(item.trafficP50 ?? 0),
      sorter: (a, b) => sorterFunc(a, b, 'trafficP50'),
    },
    {
      dataIndex: 'trafficP75',
      title: t('流量P75'),
      key: 'trafficP75',
      width: 150,
      render: (_: unknown, item) => formatByte2Mb(item.trafficP75 ?? 0),
      sorter: (a, b) => sorterFunc(a, b, 'trafficP75'),
    },
    {
      dataIndex: 'trafficP90',
      key: 'trafficP90',
      title: t('流量P90'),
      width: 150,
      render: (_: unknown, item) => formatByte2Mb(item.trafficP90 ?? 0),
      sorter: (a, b) => sorterFunc(a, b, 'trafficP90'),
    },
    {
      dataIndex: 'trafficP99',
      key: 'trafficP99',
      title: t('流量P99'),
      width: 150,
      render: (_: unknown, item) => formatByte2Mb(item.trafficP99 ?? 0),
      sorter: (a, b) => sorterFunc(a, b, 'trafficP99'),
    },
  ];


  const showDetail = () => {
    setVisible(true);
  };

  const widthColumnCount = columns!.filter(({ width }) => !width).length;
  const mergedColumns = columns!.map((column) => {
    if (column.width) {
      return column;
    }

    return {
      ...column,
      width: Math.floor(tableWidth / widthColumnCount),
    };
  });

  const gridRef = useRef<any>();
  const [connectObject] = useState<any>(() => {
    const obj = {};
    Object.defineProperty(obj, 'scrollLeft', {
      get: () => {
        if (gridRef.current) {
          return gridRef.current?.state?.scrollLeft;
        }
        return null;
      },
      set: (scrollLeft: number) => {
        if (gridRef.current) {
          gridRef.current.scrollTo({ scrollLeft });
        }
      },
    });

    return obj;
  });

  const resetVirtualGrid = () => {
    gridRef.current?.resetAfterIndices({
      columnIndex: 0,
      rowIndex: 0,
      shouldForceUpdate: true,
    });
  };

  useEffect(() => resetVirtualGrid, [tableWidth]);

  const renderVirtualList = (rawData: object[], { scrollbarSize, ref, onScroll }: any) => {
    // eslint-disable-next-line no-param-reassign
    ref.current = connectObject;
    const totalHeight = rawData.length * 54;
    return (
      <Grid
        ref={gridRef}
        className={styles.virtualTable}
        columnCount={mergedColumns.length}
        columnWidth={(index: number) => {
          const { width } = mergedColumns[index];
          return totalHeight > scroll!.y! && index === mergedColumns.length - 1
            ? (width as number) - scrollbarSize - 1
            : (width as number);
        }}
        height={scroll!.y as number}
        rowCount={rawData.length}
        rowHeight={() => 64}
        width={tableWidth}
        onScroll={({ scrollLeft }: { scrollLeft: number }) => {
          onScroll({ scrollLeft });
        }}
      >
        {({
          columnIndex,
          style,
          rowIndex,
        }: {
          columnIndex: number;
          rowIndex: number;
          style: React.CSSProperties;
        }) => <div
            className={classNames({
              [styles.virtualTableCell]: true,
              [styles.virtualTableCellLast]: (columnIndex === mergedColumns.length - 1),
            })}
            style={style}
          >
            {(mergedColumns as any)[columnIndex].render(null, rawData[rowIndex], rowIndex)}
          </div>}
      </Grid>
    );
  };

  return (
    <Fragment>
      <Modal
        closeIcon={<IconClose/>}
        visible={visible}
        width={1410}
        title={t('查看详情')}
        className={styles.detailView}
        footer={false}
        onCancel={() => setVisible(false)}
      >
        <div className={styles.header}>
          <div>
            <span>{t('总')}{countName}：{data?.total}</span>
          </div>
        </div>
        <ResizeObserver
          onResize={({ width }) => {
            setTableWidth(width);
          }}
        >
        <Table
          loading={loading}
          columns={columns}
          className={'bugly-table'}
          dataSource={data?.list}
          pagination={false}
          scroll={scroll}
          components={{
            body: renderVirtualList as any,
          }}
        />
        </ResizeObserver>
      </Modal>
      <Button type='link' onClick={showDetail}>{t('查看更多')}</Button>
    </Fragment>
  );
};
